import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createLeasing,
  deleteLeasing,
  detectLeasingFormulaFromPdf,
  fetchAccounts,
  fetchBusinessPartnerByCardCode,
  fetchCompanies,
  fetchLeasings,
  patchLeasing,
  renameLeasingContractApi,
  type Account,
  type Company,
  type InterestFormula,
  type Leasing,
} from "../api";
import { formatDateEs, formatIban, formatMoney } from "../lib/grouping";

/**
 * Vista de leasings — Fase 1: maestro manual.
 *
 * Flujo de alta:
 *  1. Empresa (dropdown).
 *  2. Cuenta bancaria de la empresa (dropdown filtrado): al elegir
 *     auto-rellena bankCode, bankName, currency.
 *  3. Nº contrato + alias.
 *  4. CardCode del proveedor. Al perder foco, la app consulta HANA
 *     (OCRD) y auto-rellena el nombre del proveedor.
 *  5. Patrón de detección en el memo del movimiento bancario.
 *
 * Al guardar, el leasing queda con las cuentas contables vacías. Las
 * rellenas después editando la fila (aparecen 6 pares cuenta+CC en una
 * sub-tabla). Fase 2 añadirá cuadro de amortización.
 */

function accountRequiresCostingCode(account: string): boolean {
  const s = (account ?? "").trim();
  return s.length > 0 && (s.startsWith("6") || s.startsWith("7"));
}

type AccountsDraft = {
  sapAccountAsset: string;
  costingCodeAsset: string;
  projectCodeAsset: string;
  sapAccountVAT: string;
  costingCodeVAT: string;
  projectCodeVAT: string;
  sapAccountLongTerm: string;
  costingCodeLongTerm: string;
  projectCodeLongTerm: string;
  sapAccountShortTerm: string;
  costingCodeShortTerm: string;
  projectCodeShortTerm: string;
  sapAccountInterest: string;
  costingCodeInterest: string;
  projectCodeInterest: string;
  sapAccountFees: string;
  costingCodeFees: string;
  projectCodeFees: string;
};

export function Leasings() {
  // Activa layout ancho (main sin max-width 1400) mientras la vista está montada.
  // Sin esto la tabla queda estrecha y las celdas hacen wrap.
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);
  const [items, setItems] = useState<Leasing[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    companyId: "",
    iban: "",
    bankCode: "",
    bankName: "",
    contractNumber: "",
    alias: "",
    currency: "EUR",
    supplier: "",
    supplierCardCode: "",
    descriptionPattern: "",
  });
  const [busy, setBusy] = useState(false);
  const [supplierMsg, setSupplierMsg] = useState<string | null>(null);
  /** Empresas colapsadas (companyId). Persistido en sessionStorage para no
   *  perderlo al navegar entre pantallas dentro de la sesión del navegador. */
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem("leasings.collapsedCompanies");
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* noop */ }
    return new Set();
  });
  const persistCollapsed = (next: Set<string>) => {
    setCollapsedCompanies(next);
    try {
      sessionStorage.setItem("leasings.collapsedCompanies", JSON.stringify(Array.from(next)));
    } catch { /* noop */ }
  };
  const toggleCollapsed = (companyId: string) => {
    const next = new Set(collapsedCompanies);
    if (next.has(companyId)) next.delete(companyId);
    else next.add(companyId);
    persistCollapsed(next);
  };

  const load = async () => {
    setError(null);
    try {
      const [l, c, a] = await Promise.all([fetchLeasings(), fetchCompanies(), fetchAccounts()]);
      setItems(l.items);
      setCompanies(c.items);
      setAccounts(a.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /**
   * Leasings agrupados por empresa (companyId). Cada grupo contiene la empresa
   * (o un "shim" con solo el id cuando la empresa no aparece en el maestro)
   * y sus leasings ordenados por bankCode+contractNumber. Los grupos se
   * ordenan alfabéticamente por alias||name para que la vista sea estable.
   */
  const groupedByCompany = useMemo(() => {
    if (!items) return null;
    const companyById = new Map(companies.map((c) => [c.id, c]));
    const byId = new Map<string, { company: Company | { id: string; name: string; alias: string }; items: Leasing[] }>();
    for (const l of items) {
      const key = l.companyId || "__no_company__";
      const existing = byId.get(key);
      if (existing) {
        existing.items.push(l);
      } else {
        const company =
          companyById.get(l.companyId) ??
          ({ id: l.companyId || "", name: l.companyId || "(sin empresa)", alias: "" } as {
            id: string;
            name: string;
            alias: string;
          });
        byId.set(key, { company, items: [l] });
      }
    }
    for (const g of byId.values()) {
      g.items.sort((a, b) =>
        (a.bankCode + a.contractNumber).localeCompare(b.bankCode + b.contractNumber),
      );
    }
    return Array.from(byId.values()).sort((a, b) => {
      const la = (a.company.alias || a.company.name || a.company.id).toLowerCase();
      const lb = (b.company.alias || b.company.name || b.company.id).toLowerCase();
      return la.localeCompare(lb, "es");
    });
  }, [items, companies]);

  const accountsForCompany = useMemo(() => {
    if (!draft.companyId) return [] as Account[];
    return accounts.filter((a) => a.active && a.companyId === draft.companyId);
  }, [accounts, draft.companyId]);

  const onSelectAccount = (iban: string) => {
    const acc = accounts.find((a) => a.iban === iban);
    if (!acc) {
      setDraft({ ...draft, iban: "", bankCode: "", bankName: "" });
      return;
    }
    setDraft({
      ...draft,
      iban: acc.iban,
      bankCode: acc.bankCode,
      bankName: acc.bankName,
      currency: acc.currency || "EUR",
    });
  };

  const lookupSupplier = async (companyId: string, cardCode: string) => {
    const cc = cardCode.trim();
    if (!companyId || !cc) {
      setSupplierMsg(null);
      return;
    }
    setSupplierMsg("⏳ Consultando SAP…");
    try {
      const bp = await fetchBusinessPartnerByCardCode(companyId, cc);
      if (!bp) {
        setSupplierMsg(`⚠️ CardCode ${cc} no encontrado en SAP.`);
        return;
      }
      setDraft((prev) => ({ ...prev, supplier: bp.cardName }));
      setSupplierMsg(`✓ ${bp.cardName}`);
    } catch (e: unknown) {
      setSupplierMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const save = async () => {
    if (!draft.companyId) {
      alert("Elige empresa.");
      return;
    }
    if (!draft.iban || !draft.bankCode) {
      alert("Elige la cuenta bancaria donde se cargarán las cuotas.");
      return;
    }
    if (!draft.contractNumber.trim()) {
      alert("Indica el número de contrato.");
      return;
    }
    setBusy(true);
    try {
      await createLeasing({
        bankCode: draft.bankCode,
        contractNumber: draft.contractNumber.trim(),
        companyId: draft.companyId,
        alias: draft.alias.trim() || undefined,
        bankName: draft.bankName.trim() || undefined,
        currency: draft.currency,
        iban: draft.iban,
        supplier: draft.supplier.trim() || undefined,
        supplierCardCode: draft.supplierCardCode.trim() || undefined,
        descriptionPattern: draft.descriptionPattern.trim() || undefined,
      });
      setDraft({
        companyId: "",
        iban: "",
        bankCode: "",
        bankName: "",
        contractNumber: "",
        alias: "",
        currency: "EUR",
        supplier: "",
        supplierCardCode: "",
        descriptionPattern: "",
      });
      setSupplierMsg(null);
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Leasings</h2>
          <p className="meta">
            Maestro manual. Elige empresa → cuenta bancaria de cargo → nº contrato →
            CardCode del proveedor (el nombre se autocompleta desde SAP). Las cuentas
            contables se rellenan al editar la fila. Fase 2 añadirá el cuadro de
            amortización editable; Fase 3 la contabilización de la factura y del pago.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            onClick={() => {
              if (!groupedByCompany) return;
              const allIds = groupedByCompany.map((g) => g.company.id || "__no_company__");
              const anyExpanded = allIds.some((id) => !collapsedCompanies.has(id));
              persistCollapsed(anyExpanded ? new Set(allIds) : new Set());
            }}
            title="Colapsar o expandir todas las empresas"
          >
            {groupedByCompany && groupedByCompany.every((g) => collapsedCompanies.has(g.company.id || "__no_company__"))
              ? "▸ Expandir todas"
              : "▾ Colapsar todas"}
          </button>
          <button type="button" onClick={() => setCreating((x) => !x)} disabled={companies.length === 0}>
            {creating ? "✕ Cancelar" : "+ Nuevo leasing"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {companies.length === 0 && (
        <p className="warning">
          No hay empresas. Crea al menos una en "Empresas" para asociar leasings.
        </p>
      )}

      {creating && companies.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.75rem 0" }}>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em" }}>
              Empresa<br />
              <select
                value={draft.companyId}
                onChange={(e) => setDraft({ ...draft, companyId: e.target.value, iban: "", bankCode: "", bankName: "", supplier: "" })}
              >
                <option value="">— Elige —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Cuenta bancaria de cargo<br />
              <select
                value={draft.iban}
                onChange={(e) => onSelectAccount(e.target.value)}
                disabled={!draft.companyId}
                style={{ minWidth: "18rem" }}
              >
                <option value="">— Elige cuenta —</option>
                {accountsForCompany.map((a) => (
                  <option key={a.iban} value={a.iban}>
                    {formatIban(a.iban)} · {a.bankCode}{a.alias ? ` · ${a.alias}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {draft.companyId && accountsForCompany.length === 0 && (
              <span className="warning" style={{ fontSize: "0.85em" }}>
                Esta empresa no tiene cuentas activas. Añade una en "Cuentas".
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em" }}>
              Nº contrato<br />
              <input value={draft.contractNumber} onChange={(e) => setDraft({ ...draft, contractNumber: e.target.value })} style={{ width: "10rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Alias<br />
              <input value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} style={{ width: "10rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              CardCode proveedor (SAP)<br />
              <input
                value={draft.supplierCardCode}
                onChange={(e) => setDraft({ ...draft, supplierCardCode: e.target.value })}
                onBlur={() => lookupSupplier(draft.companyId, draft.supplierCardCode)}
                placeholder="ej. P02968"
                style={{ width: "8rem" }}
              />
            </label>
            <label style={{ fontSize: "0.85em", flex: 1, minWidth: "12rem" }}>
              Proveedor (desde SAP)<br />
              <input
                value={draft.supplier}
                onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
                placeholder="Se autocompleta al escribir CardCode"
                style={{ width: "100%" }}
              />
              {supplierMsg && (
                <div className="meta" style={{ fontSize: "0.8em", marginTop: 2 }}>{supplierMsg}</div>
              )}
            </label>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em", flex: 1, minWidth: "16rem" }}>
              Patrón de detección del movimiento<br />
              <input
                value={draft.descriptionPattern}
                onChange={(e) => setDraft({ ...draft, descriptionPattern: e.target.value })}
                placeholder="ej. LEASING CAIXA CONTRATO 12345"
                style={{ width: "100%" }}
              />
            </label>
            <div>
              <button type="button" onClick={save} disabled={busy}>
                {busy ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && <p className="meta">No hay leasings dados de alta.</p>}
      {items && items.length > 0 && groupedByCompany && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {groupedByCompany.map((g) => {
            const displayName = g.company.alias || g.company.name || g.company.id;
            const key = g.company.id || "__no_company__";
            const collapsed = collapsedCompanies.has(key);
            // Subtotales agregados por empresa (patrón de Loans.tsx):
            //   Concedido = suma de leasing.principal
            //   Pendiente = suma de summary.pendingCapitalShortTerm + LongTerm
            // Moneda: la del primer leasing del grupo (asumimos homogénea).
            const groupCurrency = g.items[0]?.currency || "EUR";
            const totalGranted = g.items.reduce((s, l) => s + (l.principal ?? 0), 0);
            const totalPending = g.items.reduce((s, l) => {
              const cp = l.summary?.pendingCapitalShortTerm ?? 0;
              const lp = l.summary?.pendingCapitalLongTerm ?? 0;
              return s + cp + lp;
            }, 0);
            return (
              <div key={key} className="card owner-card">
                <div
                  className="card-head"
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}
                >
                  <div
                    onClick={() => toggleCollapsed(key)}
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
                    title={collapsed ? "Expandir" : "Colapsar"}
                  >
                    <span style={{ fontSize: "0.9em", width: 14, display: "inline-block" }}>
                      {collapsed ? "▸" : "▾"}
                    </span>
                    <h3 style={{ margin: 0 }}>🏢 {displayName}</h3>
                  </div>
                  {/* Subtotales agregados de la empresa — mismo patrón que Loans.tsx */}
                  <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: "0.9em" }}>
                    <span>
                      <span className="meta" style={{ marginRight: 4 }}>CONCEDIDO</span>
                      <strong>{formatMoney(totalGranted, groupCurrency)}</strong>
                    </span>
                    <span>
                      <span className="meta" style={{ marginRight: 4 }}>PENDIENTE</span>
                      <strong>{formatMoney(totalPending, groupCurrency)}</strong>
                    </span>
                    <span className="badge">
                      {g.items.length} leasing{g.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                {!collapsed && (
                <table className="data" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Cuenta cargo</th>
                      <th>Contrato</th>
                      <th>Alias</th>
                      <th>Proveedor</th>
                      <th className="num">Concedido</th>
                      <th className="num" title="Capital pendiente total (CP + LP)">Pendiente</th>
                      <th title="Siguiente cuota a pagar (fecha e importe)">Próx. cuota</th>
                      <th title="Fecha de la última cuota del valor residual">Vencimiento</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((l) => (
                      <LeasingRow
                        key={`${l.bankCode}::${l.contractNumber}`}
                        leasing={l}
                        companies={companies}
                        accounts={accounts}
                        onUpdated={load}
                        hideCompanyColumn
                      />
                    ))}
                  </tbody>
                </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function LeasingRow({
  leasing,
  companies,
  accounts,
  onUpdated,
  hideCompanyColumn,
}: {
  leasing: Leasing;
  companies: Company[];
  accounts: Account[];
  onUpdated: () => void;
  hideCompanyColumn?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [alias, setAlias] = useState(leasing.alias);
  const [companyId, setCompanyId] = useState(leasing.companyId);
  const [iban, setIban] = useState(leasing.iban);
  const [bankCode, setBankCode] = useState(leasing.bankCode);
  const [bankName, setBankName] = useState(leasing.bankName);
  const [supplier, setSupplier] = useState(leasing.supplier);
  const [supplierCardCode, setSupplierCardCode] = useState(leasing.supplierCardCode);
  const [descriptionPattern, setDescriptionPattern] = useState(leasing.descriptionPattern);
  const [vatGroup, setVatGroup] = useState(leasing.vatGroup ?? "");
  const [supplierMsg, setSupplierMsg] = useState<string | null>(null);
  const [accDraft, setAccDraft] = useState<AccountsDraft>({
    sapAccountAsset: leasing.sapAccountAsset,
    costingCodeAsset: leasing.costingCodeAsset,
    projectCodeAsset: leasing.projectCodeAsset ?? "",
    sapAccountVAT: leasing.sapAccountVAT,
    costingCodeVAT: leasing.costingCodeVAT,
    projectCodeVAT: leasing.projectCodeVAT ?? "",
    sapAccountLongTerm: leasing.sapAccountLongTerm,
    costingCodeLongTerm: leasing.costingCodeLongTerm,
    projectCodeLongTerm: leasing.projectCodeLongTerm ?? "",
    sapAccountShortTerm: leasing.sapAccountShortTerm,
    costingCodeShortTerm: leasing.costingCodeShortTerm,
    projectCodeShortTerm: leasing.projectCodeShortTerm ?? "",
    sapAccountInterest: leasing.sapAccountInterest,
    costingCodeInterest: leasing.costingCodeInterest,
    projectCodeInterest: leasing.projectCodeInterest ?? "",
    sapAccountFees: leasing.sapAccountFees,
    costingCodeFees: leasing.costingCodeFees,
    projectCodeFees: leasing.projectCodeFees ?? "",
  });
  const [contractDraft, setContractDraft] = useState({
    startDate: leasing.startDate ?? "",
    principal: leasing.principal === null ? "" : String(leasing.principal),
    installmentCount: leasing.installmentCount === null ? "" : String(leasing.installmentCount),
    interestRateAnnual: leasing.interestRateAnnual === null ? "" : String(leasing.interestRateAnnual),
    vatRatePercent: leasing.vatRatePercent === null ? "" : String(leasing.vatRatePercent),
    residualValue: leasing.residualValue === null ? "" : String(leasing.residualValue),
    postalFeeMonthly: leasing.postalFeeMonthly === null ? "" : String(leasing.postalFeeMonthly),
  });
  // Fórmula del interés (por defecto standard = francesa clásica).
  const [interestFormula, setInterestFormula] = useState<InterestFormula>(leasing.interestFormula ?? "standard");
  const contractPdfRef = useRef<HTMLInputElement | null>(null);
  const [detectMsg, setDetectMsg] = useState<string | null>(null);

  // Tramos de tipo escalonado, editados como strings para tolerar coma/punto.
  const [tranchesDraft, setTranchesDraft] = useState<Array<{
    fromInstallment: string;
    annualRate: string;
    fixedBase: string;
    initialInterest: string;
  }>>(
    (leasing.interestRateTranches ?? []).map((t) => ({
      fromInstallment: String(t.fromInstallment),
      annualRate: String(t.annualRate),
      fixedBase: t.fixedBase != null ? String(t.fixedBase) : "",
      initialInterest: t.initialInterest != null ? String(t.initialInterest) : "",
    })),
  );
  const [busy, setBusy] = useState(false);

  const parseNumOrNull = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const companyLabel = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.alias || c.name) : id || "—";
  };

  const accountsForEditingCompany = useMemo(
    () => accounts.filter((a) => a.active && a.companyId === companyId),
    [accounts, companyId],
  );

  const startEditing = () => {
    setAlias(leasing.alias);
    setCompanyId(leasing.companyId);
    setIban(leasing.iban);
    setBankCode(leasing.bankCode);
    setBankName(leasing.bankName);
    setSupplier(leasing.supplier);
    setSupplierCardCode(leasing.supplierCardCode);
    setDescriptionPattern(leasing.descriptionPattern);
    setVatGroup(leasing.vatGroup ?? "");
    setSupplierMsg(null);
    setAccDraft({
      sapAccountAsset: leasing.sapAccountAsset,
      costingCodeAsset: leasing.costingCodeAsset,
      projectCodeAsset: leasing.projectCodeAsset ?? "",
      sapAccountVAT: leasing.sapAccountVAT,
      costingCodeVAT: leasing.costingCodeVAT,
      projectCodeVAT: leasing.projectCodeVAT ?? "",
      sapAccountLongTerm: leasing.sapAccountLongTerm,
      costingCodeLongTerm: leasing.costingCodeLongTerm,
      projectCodeLongTerm: leasing.projectCodeLongTerm ?? "",
      sapAccountShortTerm: leasing.sapAccountShortTerm,
      costingCodeShortTerm: leasing.costingCodeShortTerm,
      projectCodeShortTerm: leasing.projectCodeShortTerm ?? "",
      sapAccountInterest: leasing.sapAccountInterest,
      costingCodeInterest: leasing.costingCodeInterest,
      projectCodeInterest: leasing.projectCodeInterest ?? "",
      sapAccountFees: leasing.sapAccountFees,
      costingCodeFees: leasing.costingCodeFees,
      projectCodeFees: leasing.projectCodeFees ?? "",
    });
    setContractDraft({
      startDate: leasing.startDate ?? "",
      principal: leasing.principal === null ? "" : String(leasing.principal),
      installmentCount: leasing.installmentCount === null ? "" : String(leasing.installmentCount),
      interestRateAnnual: leasing.interestRateAnnual === null ? "" : String(leasing.interestRateAnnual),
      vatRatePercent: leasing.vatRatePercent === null ? "" : String(leasing.vatRatePercent),
      residualValue: leasing.residualValue === null ? "" : String(leasing.residualValue),
      postalFeeMonthly: leasing.postalFeeMonthly === null ? "" : String(leasing.postalFeeMonthly),
    });
    setTranchesDraft(
      (leasing.interestRateTranches ?? []).map((t) => ({
        fromInstallment: String(t.fromInstallment),
        annualRate: String(t.annualRate),
        fixedBase: t.fixedBase != null ? String(t.fixedBase) : "",
        initialInterest: t.initialInterest != null ? String(t.initialInterest) : "",
      })),
    );
    setInterestFormula(leasing.interestFormula ?? "standard");
    setDetectMsg(null);
    setEditing(true);
  };

  const onSelectAccount = (newIban: string) => {
    const acc = accounts.find((a) => a.iban === newIban);
    setIban(newIban);
    if (acc) {
      setBankCode(acc.bankCode);
      setBankName(acc.bankName);
    }
  };

  const lookupSupplier = async () => {
    const cc = supplierCardCode.trim();
    if (!companyId || !cc) {
      setSupplierMsg(null);
      return;
    }
    setSupplierMsg("⏳ Consultando SAP…");
    try {
      const bp = await fetchBusinessPartnerByCardCode(companyId, cc);
      if (!bp) {
        setSupplierMsg(`⚠️ CardCode ${cc} no encontrado en SAP.`);
        return;
      }
      setSupplier(bp.cardName);
      setSupplierMsg(`✓ ${bp.cardName}`);
    } catch (e: unknown) {
      setSupplierMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const detectFormula = async (file: File) => {
    setDetectMsg("⏳ Analizando PDF…");
    try {
      const r = await detectLeasingFormulaFromPdf(leasing.bankCode, leasing.contractNumber, file);
      const label = formulaLabel(r.suggested);
      setInterestFormula(r.suggested);
      setDetectMsg(
        `✓ Sugerida: ${label} (confianza ${r.confidence}). ${r.reasoning}`,
      );
    } catch (e: unknown) {
      setDetectMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      if (contractPdfRef.current) contractPdfRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await patchLeasing(leasing.bankCode, leasing.contractNumber, {
        alias,
        companyId,
        iban,
        bankCode,
        bankName,
        supplier,
        supplierCardCode,
        descriptionPattern,
        vatGroup,
        ...accDraft,
        startDate: contractDraft.startDate.trim() || null,
        principal: parseNumOrNull(contractDraft.principal),
        installmentCount: parseNumOrNull(contractDraft.installmentCount),
        interestRateAnnual: parseNumOrNull(contractDraft.interestRateAnnual),
        vatRatePercent: parseNumOrNull(contractDraft.vatRatePercent),
        residualValue: parseNumOrNull(contractDraft.residualValue),
        postalFeeMonthly: parseNumOrNull(contractDraft.postalFeeMonthly),
        interestFormula,
        interestRateTranches: (() => {
          const parseMoney = (s: string): number => Number((s || "").replace(",", "."));
          const cleaned: Array<{
            fromInstallment: number;
            annualRate: number;
            fixedBase?: number;
            initialInterest?: number;
          }> = [];
          for (const t of tranchesDraft) {
            const from = Math.floor(Number(t.fromInstallment));
            const rate = parseMoney(t.annualRate);
            if (!Number.isFinite(from) || from < 1) continue;
            const entry: {
              fromInstallment: number;
              annualRate: number;
              fixedBase?: number;
              initialInterest?: number;
            } = {
              fromInstallment: from,
              annualRate: Number.isFinite(rate) && rate >= 0 ? rate : 0,
            };
            const fb = parseMoney(t.fixedBase);
            if (Number.isFinite(fb) && fb > 0) entry.fixedBase = fb;
            const ii = parseMoney(t.initialInterest);
            if (Number.isFinite(ii) && ii >= 0 && t.initialInterest.trim()) entry.initialInterest = ii;
            cleaned.push(entry);
          }
          cleaned.sort((a, b) => a.fromInstallment - b.fromInstallment);
          return cleaned.length > 0 ? cleaned : null;
        })(),
      });
      setEditing(false);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`¿Borrar el leasing ${leasing.contractNumber}?`)) return;
    setBusy(true);
    try {
      await deleteLeasing(leasing.bankCode, leasing.contractNumber);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onRename = async () => {
    const nuevo = prompt(
      `Corregir nº contrato del leasing (banco ${leasing.bankCode}).\nActual: ${leasing.contractNumber}`,
      leasing.contractNumber,
    );
    if (nuevo === null) return;
    const trimmed = nuevo.trim();
    if (!trimmed || trimmed === leasing.contractNumber) return;
    setBusy(true);
    try {
      await renameLeasingContractApi(leasing.bankCode, leasing.contractNumber, trimmed);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await patchLeasing(leasing.bankCode, leasing.contractNumber, { active: !leasing.active });
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr style={leasing.active ? undefined : { opacity: 0.5 }}>
        {!hideCompanyColumn && (
          <td>
            {editing ? (
              <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setIban(""); setBankCode(""); setBankName(""); }}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            ) : companyLabel(leasing.companyId)}
          </td>
        )}
        <td>
          {editing ? (
            <select value={iban} onChange={(e) => onSelectAccount(e.target.value)} style={{ minWidth: "16rem" }}>
              <option value="">— Elige cuenta —</option>
              {accountsForEditingCompany.map((a) => (
                <option key={a.iban} value={a.iban}>{formatIban(a.iban)} · {a.bankCode}</option>
              ))}
            </select>
          ) : leasing.iban ? (
            <span title={`${leasing.bankCode}${leasing.bankName ? ` · ${leasing.bankName}` : ""}`}>
              {formatIban(leasing.iban)}
            </span>
          ) : (
            <span className="meta">— sin cuenta</span>
          )}
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          {leasing.contractNumber}
          {!editing && (
            <button
              type="button"
              onClick={onRename}
              disabled={busy}
              title="Corregir nº de contrato (por error tipográfico)"
              style={{ marginLeft: 6, fontSize: "0.7em", padding: "1px 5px" }}
            >
              ✎
            </button>
          )}
        </td>
        <td>
          {editing
            ? <input value={alias} onChange={(e) => setAlias(e.target.value)} style={{ width: "10rem" }} />
            : (leasing.alias || <span className="meta">—</span>)}
        </td>
        {/* Proveedor + CardCode en una sola línea, separados por ·. */}
        <td style={{ whiteSpace: "nowrap" }}>
          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Se autocompleta" style={{ width: "12rem" }} />
              <input
                value={supplierCardCode}
                onChange={(e) => setSupplierCardCode(e.target.value)}
                onBlur={lookupSupplier}
                placeholder="CardCode"
                style={{ width: "7rem" }}
              />
            </div>
          ) : leasing.supplier || leasing.supplierCardCode ? (
            <span>
              {leasing.supplier || "—"}
              {leasing.supplierCardCode ? ` · ${leasing.supplierCardCode}` : ""}
            </span>
          ) : (
            <span className="meta">—</span>
          )}
        </td>
        {/* Concedido = principal del contrato (rellenado manualmente al crear
            el leasing). */}
        <td className="num" style={{ whiteSpace: "nowrap" }}>
          {leasing.principal !== null && leasing.principal !== undefined
            ? formatMoney(leasing.principal, leasing.currency)
            : <span className="meta">—</span>}
        </td>
        {/* Pendiente = CP + LP del cuadro. Total arriba, desglose CP/LP como
            meta debajo (visible siempre). */}
        <td className="num" style={{ whiteSpace: "nowrap" }}>
          {leasing.summary ? (
            <>
              <div>
                {formatMoney(
                  leasing.summary.pendingCapitalShortTerm + leasing.summary.pendingCapitalLongTerm,
                  leasing.currency,
                )}
              </div>
              {(leasing.summary.pendingCapitalShortTerm > 0 || leasing.summary.pendingCapitalLongTerm > 0) && (
                <div className="meta" style={{ fontSize: "0.75em" }}>
                  CP {formatMoney(leasing.summary.pendingCapitalShortTerm, leasing.currency)}
                  {" · "}
                  LP {formatMoney(leasing.summary.pendingCapitalLongTerm, leasing.currency)}
                </div>
              )}
            </>
          ) : (
            <span className="meta">—</span>
          )}
        </td>
        {/* Próxima cuota: fecha + importe en una sola línea con · separador. */}
        <td style={{ whiteSpace: "nowrap" }}>
          {leasing.summary?.nextInstallmentDate ? (
            <>
              {formatDateEs(leasing.summary.nextInstallmentDate.slice(0, 10))}
              {leasing.summary.nextInstallmentAmount !== null && (
                <> · {formatMoney(leasing.summary.nextInstallmentAmount, leasing.currency)}</>
              )}
            </>
          ) : (
            <span className="meta">—</span>
          )}
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          {leasing.summary?.residualDate
            ? formatDateEs(leasing.summary.residualDate.slice(0, 10))
            : <span className="meta">—</span>}
        </td>
        <td className="actions" style={{ whiteSpace: "nowrap" }}>
          {editing ? (
            <>
              <button type="button" onClick={save} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 8px" }}>OK</button>
              <button type="button" onClick={() => setEditing(false)} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 6px" }}>✕</button>
            </>
          ) : (
            <>
              <Link
                to={`/leasings/${encodeURIComponent(leasing.bankCode)}/${encodeURIComponent(leasing.contractNumber)}/cuadro`}
              >
                <button type="button" title="Cuadro de amortización" style={{ fontSize: "0.8em", padding: "2px 8px" }}>Cuadro</button>
              </Link>
              <button type="button" onClick={toggleActive} disabled={busy} title={leasing.active ? "Desactivar" : "Activar"} style={{ fontSize: "0.8em", padding: "2px 6px" }}>
                {leasing.active ? "🟢" : "⚪"}
              </button>
              <button type="button" onClick={startEditing} title="Editar" style={{ fontSize: "0.8em", padding: "2px 6px" }}>✏️</button>
              <button type="button" className="danger" onClick={onDelete} disabled={busy} title="Borrar" style={{ fontSize: "0.8em", padding: "2px 6px" }}>🗑️</button>
            </>
          )}
        </td>
      </tr>
      {!editing && leasing.descriptionPattern && (
        <tr style={{ ...(leasing.active ? {} : { opacity: 0.5 }), background: "rgba(80,160,255,0.04)" }}>
          <td colSpan={hideCompanyColumn ? 9 : 10} style={{ fontSize: "0.8em", padding: "0.15rem 0.75rem", color: "var(--meta, #888)" }}>
            🔍 Patrón detección: <code>{leasing.descriptionPattern}</code>
          </td>
        </tr>
      )}
      {editing && (
        <tr>
          <td colSpan={hideCompanyColumn ? 9 : 10} style={{ background: "rgba(80,160,255,0.06)", padding: "0.75rem 1rem" }}>
            {supplierMsg && (
              <div className="meta" style={{ fontSize: "0.8em", marginBottom: "0.5rem" }}>{supplierMsg}</div>
            )}
            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Patrón de detección del movimiento</h4>
              <p className="meta" style={{ margin: "0 0 0.4rem 0", fontSize: "0.85em" }}>
                Texto (case-insensitive) que aparece en la descripción del movimiento
                del banco cuando se cobra una cuota de este leasing. Se usará en la
                Fase 4 para detectar automáticamente.
              </p>
              <input
                type="text"
                value={descriptionPattern}
                onChange={(e) => setDescriptionPattern(e.target.value)}
                placeholder="ej. LEASING CAIXA CONTRATO 12345"
                style={{ width: "100%", maxWidth: 480 }}
              />
              <div style={{ marginTop: "0.5rem" }}>
                <label style={{ fontSize: "0.85em", display: "block", marginBottom: 4 }}>
                  Código IVA (VatGroup) para la factura del proveedor
                </label>
                <input
                  type="text"
                  value={vatGroup}
                  onChange={(e) => setVatGroup(e.target.value)}
                  placeholder="ej. IB1 / S21 / IVA21 — vacío = SAP lo deduce"
                  style={{ width: "12rem" }}
                />
              </div>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Parámetros del contrato</h4>
              <p className="meta" style={{ margin: "0 0 0.4rem 0", fontSize: "0.85em" }}>
                Necesarios para generar el cuadro de amortización (Fase 2).
                Al guardar, ve a "Cuadro" y pulsa "Regenerar" para calcularlo.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center", fontSize: "0.85em" }}>
                <label>Inicio <input type="date" value={contractDraft.startDate} onChange={(e) => setContractDraft({ ...contractDraft, startDate: e.target.value })} /></label>
                <label title="Capital financiado sin IVA">Importe contrato <input value={contractDraft.principal} onChange={(e) => setContractDraft({ ...contractDraft, principal: e.target.value })} placeholder="capital" style={{ width: "7rem" }} /></label>
                <label>Nº cuotas <input value={contractDraft.installmentCount} onChange={(e) => setContractDraft({ ...contractDraft, installmentCount: e.target.value })} style={{ width: "5.5rem" }} /></label>
                <label>Tipo % anual <input value={contractDraft.interestRateAnnual} onChange={(e) => setContractDraft({ ...contractDraft, interestRateAnnual: e.target.value })} style={{ width: "5.5rem" }} /></label>
                <label>IVA % <input value={contractDraft.vatRatePercent} onChange={(e) => setContractDraft({ ...contractDraft, vatRatePercent: e.target.value })} style={{ width: "4rem" }} /></label>
                <label title="Cuota residual final, sin intereses">Valor residual <input value={contractDraft.residualValue} onChange={(e) => setContractDraft({ ...contractDraft, residualValue: e.target.value })} placeholder="opcional" style={{ width: "6rem" }} /></label>
                <label title="Envío mensual del banco (sin IVA)">Envío/mes <input value={contractDraft.postalFeeMonthly} onChange={(e) => setContractDraft({ ...contractDraft, postalFeeMonthly: e.target.value })} placeholder="opcional" style={{ width: "5rem" }} /></label>
              </div>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Fórmula de cálculo del interés</h4>
              <p className="meta" style={{ margin: "0 0 0.4rem 0", fontSize: "0.85em" }}>
                La mayoría de bancos usan la fórmula estándar (francesa clásica). Caja Rural del Sur
                y equivalentes usan una variante donde se resta la cuota antes del interés. Sube el
                PDF del contrato para que la app la detecte automáticamente.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center", fontSize: "0.85em" }}>
                <label>
                  Fórmula&nbsp;
                  <select
                    value={interestFormula}
                    onChange={(e) => setInterestFormula(e.target.value as InterestFormula)}
                  >
                    <option value="standard">Estándar (francesa): In = CP × ik</option>
                    <option value="bank_next">Caja Rural del Sur: In = (CP − cuota) × ik</option>
                    <option value="average">Saldo medio: In = ((CP prev + CP curr)/2) × ik</option>
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => contractPdfRef.current?.click()}
                  title="Sube el PDF del contrato; la app extrae el texto y sugiere la fórmula que aplica."
                >
                  📄 Detectar desde PDF del contrato
                </button>
                <input
                  ref={contractPdfRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void detectFormula(f);
                  }}
                />
              </div>
              {detectMsg && (
                <p className="meta" style={{ fontSize: "0.85em", marginTop: 6 }}>{detectMsg}</p>
              )}
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Tramos de tipo escalonado (opcional)</h4>
              <p className="meta" style={{ margin: "0 0 0.4rem 0", fontSize: "0.85em" }}>
                Para cada tramo puedes especificar <strong>Cuota fija</strong> (€) e <strong>Interés inicial</strong> (€
                del primer mes del tramo). Con esos dos datos el generador reconstruye el cuadro exacto:
                deduce el tipo del tramo como <code>interés inicial / capital pendiente</code> y usa la cuota fija
                como base del tramo (sin francesa).
                Si dejas Cuota fija vacía se calcula por francesa con el tipo indicado.
                Si dejas Interés inicial vacío se usa el "Tipo % anual" del tramo.
              </p>
              {tranchesDraft.length === 0 && (
                <p className="meta" style={{ fontSize: "0.85em" }}>Sin tramos definidos (tipo fijo del contrato).</p>
              )}
              {tranchesDraft.length > 0 && (
                <table className="data" style={{ fontSize: "0.85em", maxWidth: 640 }}>
                  <thead>
                    <tr>
                      <th className="num" title="Nº de cuota desde la que aplica este tramo">Desde cuota</th>
                      <th className="num" title="% anual (usado si NO indicas Interés inicial)">Tipo % anual</th>
                      <th className="num" title="Cuota fija del tramo (€) — sobrescribe la francesa">Cuota fija €</th>
                      <th className="num" title="Interés del primer mes del tramo (€) — el tipo se deduce">Interés inicial €</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tranchesDraft.map((t, i) => (
                      <tr key={i}>
                        <td className="num">
                          <input
                            value={t.fromInstallment}
                            onChange={(e) => {
                              const copy = [...tranchesDraft];
                              copy[i] = { ...copy[i]!, fromInstallment: e.target.value };
                              setTranchesDraft(copy);
                            }}
                            style={{ width: "5rem" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            value={t.annualRate}
                            onChange={(e) => {
                              const copy = [...tranchesDraft];
                              copy[i] = { ...copy[i]!, annualRate: e.target.value };
                              setTranchesDraft(copy);
                            }}
                            style={{ width: "5rem" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            value={t.fixedBase}
                            onChange={(e) => {
                              const copy = [...tranchesDraft];
                              copy[i] = { ...copy[i]!, fixedBase: e.target.value };
                              setTranchesDraft(copy);
                            }}
                            placeholder="p.ej. 847,56"
                            style={{ width: "7rem" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            value={t.initialInterest}
                            onChange={(e) => {
                              const copy = [...tranchesDraft];
                              copy[i] = { ...copy[i]!, initialInterest: e.target.value };
                              setTranchesDraft(copy);
                            }}
                            placeholder="p.ej. 79,04"
                            style={{ width: "7rem" }}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => setTranchesDraft(tranchesDraft.filter((_, j) => j !== i))}
                            title="Eliminar tramo"
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button
                type="button"
                onClick={() => setTranchesDraft([
                  ...tranchesDraft,
                  { fromInstallment: "", annualRate: "", fixedBase: "", initialInterest: "" },
                ])}
                style={{ marginTop: 6 }}
              >
                + Añadir tramo
              </button>
            </div>

            <LeasingAccountsEditor draft={accDraft} onChange={setAccDraft} />
          </td>
        </tr>
      )}
    </>
  );
}

function formulaLabel(f: InterestFormula): string {
  switch (f) {
    case "bank_next": return "Caja Rural del Sur (In = (CP − cuota) × ik)";
    case "average":   return "Saldo medio (In = ((CP prev + CP curr)/2) × ik)";
    case "standard":
    default:          return "Estándar francesa (In = CP × ik)";
  }
}

function LeasingAccountsEditor({
  draft,
  onChange,
}: {
  draft: AccountsDraft;
  onChange: (d: AccountsDraft) => void;
}) {
  const rows: Array<{
    label: string;
    hint: string;
    accKey: keyof AccountsDraft;
    ccKey: keyof AccountsDraft;
    pjKey: keyof AccountsDraft;
  }> = [
    { label: "Activo (elemento del leasing)", hint: "p.ej. 218", accKey: "sapAccountAsset", ccKey: "costingCodeAsset", pjKey: "projectCodeAsset" },
    { label: "IVA soportado", hint: "p.ej. 472", accKey: "sapAccountVAT", ccKey: "costingCodeVAT", pjKey: "projectCodeVAT" },
    { label: "Deuda largo plazo", hint: "p.ej. 524", accKey: "sapAccountLongTerm", ccKey: "costingCodeLongTerm", pjKey: "projectCodeLongTerm" },
    { label: "Deuda corto plazo", hint: "p.ej. 174", accKey: "sapAccountShortTerm", ccKey: "costingCodeShortTerm", pjKey: "projectCodeShortTerm" },
    { label: "Intereses", hint: "p.ej. 6624", accKey: "sapAccountInterest", ccKey: "costingCodeInterest", pjKey: "projectCodeInterest" },
    { label: "Comisiones / envío", hint: "p.ej. 669", accKey: "sapAccountFees", ccKey: "costingCodeFees", pjKey: "projectCodeFees" },
  ];
  return (
    <div>
      <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Cuentas contables del leasing</h4>
      <p className="meta" style={{ margin: "0 0 0.5rem 0", fontSize: "0.85em" }}>
        Se usarán en la Fase 3 al contabilizar la factura del leasing. Las cuentas
        que empiezan por 6 o 7 requieren centro de coste Y proyecto (validación en backend).
      </p>
      <table style={{ width: "100%", fontSize: "0.85em" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Concepto</th>
            <th style={{ textAlign: "left" }}>Cuenta SAP</th>
            <th style={{ textAlign: "left" }}>Centro de coste</th>
            <th style={{ textAlign: "left" }}>Proyecto</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const acc = draft[r.accKey];
            const cc = draft[r.ccKey];
            const pj = draft[r.pjKey];
            const requiresCC = accountRequiresCostingCode(acc);
            const missingCC = requiresCC && !cc.trim();
            const missingPJ = requiresCC && !pj.trim();
            return (
              <tr key={r.accKey}>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>{r.label}</td>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>
                  <input
                    placeholder={r.hint}
                    value={acc}
                    onChange={(e) => onChange({ ...draft, [r.accKey]: e.target.value })}
                    style={{ width: "10rem" }}
                  />
                </td>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>
                  <input
                    placeholder={requiresCC ? "obligatorio" : "opcional"}
                    value={cc}
                    onChange={(e) => onChange({ ...draft, [r.ccKey]: e.target.value })}
                    style={{
                      width: "8rem",
                      borderColor: missingCC ? "#c62828" : undefined,
                    }}
                  />
                  {missingCC && (
                    <span style={{ color: "#c62828", marginLeft: 6, fontSize: "0.9em" }}>⚠</span>
                  )}
                </td>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>
                  <input
                    placeholder={requiresCC ? "obligatorio" : "opcional"}
                    value={pj}
                    onChange={(e) => onChange({ ...draft, [r.pjKey]: e.target.value })}
                    style={{
                      width: "8rem",
                      borderColor: missingPJ ? "#c62828" : undefined,
                    }}
                  />
                  {missingPJ && (
                    <span style={{ color: "#c62828", marginLeft: 6, fontSize: "0.9em" }}>⚠</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
